import { useState, useEffect } from 'react';
import { useUser } from '../contexts/UserContextNew';
import { OrganizationService } from '../services/organizationService';
import { 
  UserPermissions, 
  Organization, 
  OrganizationMembership, 
  OrganizationPermission 
} from '../types/user';

export interface OrganizationPermissionsHook {
  permissions: UserPermissions | null;
  userOrganizations: Organization[];
  primaryOrganization: Organization | null;
  membership: OrganizationMembership | null;
  isOrgAdmin: boolean;
  loading: boolean;
  error: string | null;

  // Permission checkers
  canAccessAiAnalysis: boolean;
  canAccessAdvancedAnalytics: boolean;
  canManageOrganization: boolean;
  canManageOrgMembers: boolean;
  canSetOrgPermissions: boolean;

  // Actions
  refreshPermissions: () => Promise<void>;
  checkPermission: (permission: keyof UserPermissions['effective_permissions']) => boolean;
}

export const useOrganizationPermissions = (): OrganizationPermissionsHook => {
  const { effectiveUserId, effectiveUserProfile } = useUser();
  const [permissions, setPermissions] = useState<UserPermissions | null>(null);
  const [userOrganizations, setUserOrganizations] = useState<Organization[]>([]);
  const [primaryOrganization, setPrimaryOrganization] = useState<Organization | null>(null);
  const [membership, setMembership] = useState<OrganizationMembership | null>(null);
  const [isOrgAdmin, setIsOrgAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadPermissions = async () => {
    if (!effectiveUserId || !effectiveUserProfile) {
      setPermissions(null);
      setUserOrganizations([]);
      setPrimaryOrganization(null);
      setMembership(null);
      setIsOrgAdmin(false);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Load user permissions (this works for all users)
      const userPermissions = await OrganizationService.getUserPermissions(effectiveUserId);
      setPermissions(userPermissions);

      // Only query organizations if user has an organization_id or organization_memberships
      const hasOrgMembership = effectiveUserProfile.organization_id ||
        (effectiveUserProfile.organization_memberships && effectiveUserProfile.organization_memberships.length > 0);

      if (!hasOrgMembership) {
        // User is not in any organization - set empty state without querying
        setUserOrganizations([]);
        setPrimaryOrganization(null);
        setMembership(null);
        setIsOrgAdmin(false);
        setLoading(false);
        return;
      }

      // Load user organizations
      const organizations = await OrganizationService.getUserOrganizations(effectiveUserId);
      setUserOrganizations(organizations);

      // Set primary organization
      const primaryOrg = organizations.find(org => org.id === effectiveUserProfile.organization_id) || organizations[0] || null;
      setPrimaryOrganization(primaryOrg);

      // Load membership details for primary organization
      if (primaryOrg) {
        const membershipData = await OrganizationService.getUserMembership(primaryOrg.id, effectiveUserId);
        setMembership(membershipData);

        // Check if user is admin of this organization
        const isAdmin = await OrganizationService.isUserOrganizationAdmin(primaryOrg.id, effectiveUserId);
        setIsOrgAdmin(isAdmin);
      } else {
        setMembership(null);
        setIsOrgAdmin(false);
      }

    } catch (err) {      setError(err instanceof Error ? err.message : 'Failed to load permissions');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPermissions();
  }, [effectiveUserId, effectiveUserProfile?.organization_id]);

  const checkPermission = (permission: keyof UserPermissions['effective_permissions']): boolean => {
    return permissions?.effective_permissions[permission] || false;
  };

  const refreshPermissions = async () => {
    await loadPermissions();
  };

  // AI Analysis entitlement is resolved server-side from the district contract
  // (BILLING_SPEC §5.3); individual subscriptions are retired (§7). What is
  // left here is the organization permission, which the server treats as a
  // hint rather than a gate.
  const canAccessAiAnalysis = checkPermission('can_access_ai_analysis');

  return {
    permissions,
    userOrganizations,
    primaryOrganization,
    membership,
    isOrgAdmin,
    loading,
    error,

    // Permission checkers for common use cases
    canAccessAiAnalysis,
    canAccessAdvancedAnalytics: checkPermission('can_access_advanced_analytics'),
    canManageOrganization: checkPermission('can_manage_organizations'),
    canManageOrgMembers: checkPermission('can_manage_organization_members'),
    canSetOrgPermissions: checkPermission('can_set_organization_permissions'),

    // Actions
    refreshPermissions,
    checkPermission,
  };
};

// Hook for checking a specific permission without loading full data
export const usePermissionCheck = (permission: keyof UserPermissions['effective_permissions']) => {
  const [hasPermission, setHasPermission] = useState(false);
  const [loading, setLoading] = useState(true);
  const { effectiveUserId } = useUser();

  useEffect(() => {
    const checkPermission = async () => {
      if (!effectiveUserId) {
        setHasPermission(false);
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const result = await OrganizationService.checkUserPermission(effectiveUserId, permission);
        setHasPermission(result);
      } catch (error) {        setHasPermission(false);
      } finally {
        setLoading(false);
      }
    };

    checkPermission();
  }, [effectiveUserId, permission]);

  return { hasPermission, loading };
};

// Hook for organization admins to manage permissions
export const useOrganizationManagement = (organizationId?: string) => {
  const { effectiveUserId } = useUser();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const updateUserPermissions = async (
    userId: string, 
    permissions: OrganizationPermission[]
  ) => {
    if (!organizationId || !effectiveUserId) return;

    try {
      setLoading(true);
      setError(null);

      // Verify user is admin of this organization
      const isAdmin = await OrganizationService.isUserOrganizationAdmin(organizationId, effectiveUserId);
      if (!isAdmin) {
        throw new Error('You do not have permission to manage this organization');
      }

      await OrganizationService.updateUserPermissions(organizationId, userId, permissions);
    } catch (err) {      setError(err instanceof Error ? err.message : 'Failed to update permissions');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const setOrganizationPermissions = async (permissions: OrganizationPermission[]) => {
    if (!organizationId || !effectiveUserId) return;

    try {
      setLoading(true);
      setError(null);

      // This should only be callable by super_admins, but we'll check org admin too
      const isAdmin = await OrganizationService.isUserOrganizationAdmin(organizationId, effectiveUserId);
      if (!isAdmin) {
        throw new Error('You do not have permission to set organization permissions');
      }

      await OrganizationService.setOrganizationPermissions(organizationId, permissions);
    } catch (err) {      setError(err instanceof Error ? err.message : 'Failed to set permissions');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const removeUserFromOrganization = async (userId: string) => {
    if (!organizationId || !effectiveUserId) return;

    try {
      setLoading(true);
      setError(null);

      const isAdmin = await OrganizationService.isUserOrganizationAdmin(organizationId, effectiveUserId);
      if (!isAdmin) {
        throw new Error('You do not have permission to remove users from this organization');
      }

      await OrganizationService.removeUserFromOrganization(organizationId, userId);
    } catch (err) {      setError(err instanceof Error ? err.message : 'Failed to remove user');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  return {
    updateUserPermissions,
    setOrganizationPermissions,
    removeUserFromOrganization,
    loading,
    error
  };
};